import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import ReviewsSection from '../components/ReviewsSection'
import { updateMetaTags } from '../utils/seo'

const HomePage: React.FC = () => {
  const navigate = useNavigate()

  useEffect(() => {
    updateMetaTags({
      title: 'PhoneMedic – MacBook Platinen-Reparatur & iPhone Reparatur | Bundesweit',
      description: 'MacBook startet nicht? Wasserschaden? PhoneMedic repariert MacBook & iPhone Platinen auf Chip-Ebene. ✓ Mikrochip-Löten ✓ 12 Monate Garantie ✓ Express 24h ✓ Bundesweiter Versand',
      keywords: ['MacBook Platinen Reparatur', 'MacBook startet nicht', 'MacBook Wasserschaden', 'Mikrochip Löten', 'iPhone Reparatur', 'MacBook Reparatur', 'PhoneMedic'],
      canonicalUrl: 'https://www.phone-medic.de',
    })
  }, [])

  const scrollToTop = () => setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50)

  const go = (path: string) => { navigate(path); scrollToTop() }

  const mainServices = [
    {
      icon: '🔬',
      title: 'MacBook Platinen-Reparatur',
      subtitle: 'Mikrochip-Löten',
      description: 'MacBook startet nicht oder Wasserschaden? Wir reparieren Platinen auf Chip-Ebene – auch wenn andere Werkstätten aufgeben.',
      path: '/macbook-reparatur',
      cta: 'Mehr erfahren',
      highlight: true,
    },
    {
      icon: '💻',
      title: 'MacBook Reparatur',
      subtitle: 'Pro & Air · alle Modelle',
      description: 'Display, Akku, Tastatur – professionelle MacBook Reparatur für alle Modelle von M4 bis Intel.',
      path: '/macbook-reparatur?tab=reparatur',
      cta: 'Zu den Preisen',
      highlight: false,
    },
    {
      icon: '📱',
      title: 'iPhone & Android',
      subtitle: 'Display, Akku & mehr',
      description: 'Schneller Einsende-Service für iPhone, Samsung, Google und mehr. Express 24h verfügbar.',
      path: '/repair',
      cta: 'Reparatur starten',
      highlight: false,
    },
  ]

  const whyUs = [
    {
      icon: '🔬',
      title: 'Mikrochip-Löten',
      description: 'Chip-Level Reparaturen unter dem Mikroskop – was andere nicht können.',
    },
    {
      icon: '⚡',
      title: '24h Express',
      description: 'Express-Service verfügbar. Dein Gerät repariert in nur 24 Stunden.',
    },
    {
      icon: '📦',
      title: 'Bundesweiter Versand',
      description: 'Kostenloses DHL-Versandlabel ab 50 €. Einfach einsenden, repariert zurückbekommen.',
    },
    {
      icon: '🛡️',
      title: '12 Monate Garantie',
      description: 'Auf alle Reparaturen. Keine versteckten Kosten, transparente Preise.',
    },
  ]

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section
        className="relative text-white"
        style={{ backgroundImage: 'url(/hero-bg.webp)', backgroundSize: 'cover', backgroundPosition: 'center top' }}
      >
        <div className="absolute inset-0 bg-gray-900/80" />
        <div className="relative max-w-7xl mx-auto px-4 py-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-indigo-600/20 border border-indigo-500/30 text-indigo-300 text-sm font-medium px-3 py-1 rounded-full mb-6">
              <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full"></span>
              Spezialist für MacBook Platinen-Reparatur
            </div>
            <h1 className="text-5xl font-bold mb-6 leading-tight">
              Professionelle Reparatur –<br />
              <span className="text-indigo-400">auch wenn andere aufgeben</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl">
              Deutschlandweiter Einsende-Service für MacBook und iPhone.
              Spezialisiert auf Platinen-Reparatur und Mikrochip-Löten bei Wasserschäden und Kurzschlüssen.
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => go('/platinen-reparatur')}
                className="bg-indigo-600 text-white px-7 py-3.5 rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
              >
                Platinen-Reparatur anfragen
              </button>
              <button
                onClick={() => go('/repair')}
                className="bg-white/10 text-white border border-white/20 px-7 py-3.5 rounded-lg font-semibold hover:bg-white/20 transition-colors"
              >
                Alle Reparaturen & Preise
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="bg-gray-800 text-white py-5 border-t border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            {[
              { number: '2.000+', label: 'Reparaturen' },
              { number: '24h', label: 'Express-Service' },
              { number: '12 Monate', label: 'Garantie' },
              { number: 'ab 129 €', label: 'Platinen-Reparatur' },
            ].map((stat, i) => (
              <div key={i}>
                <div className="text-2xl font-bold text-indigo-400">{stat.number}</div>
                <div className="text-sm text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Unsere Leistungen</h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              Vom einfachen Displaytausch bis zur komplexen Platinen-Reparatur – alles aus einer Hand
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mainServices.map((service, index) => (
              <div
                key={index}
                className={`bg-white rounded-xl border p-6 flex flex-col hover:shadow-md transition-shadow cursor-pointer ${
                  service.highlight
                    ? 'border-indigo-200 ring-1 ring-indigo-200'
                    : 'border-gray-200'
                }`}
                onClick={() => go(service.path)}
              >
                <h3 className="text-lg font-bold text-gray-900 mb-0.5">{service.title}</h3>
                <p className="text-sm text-indigo-600 font-medium mb-3">{service.subtitle}</p>
                <p className="text-sm text-gray-500 flex-1 mb-5">{service.description}</p>
                <button
                  className={`text-sm font-semibold px-4 py-2 rounded-lg transition-colors ${
                    service.highlight
                      ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                      : 'bg-gray-900 text-white hover:bg-gray-700'
                  }`}
                >
                  {service.cta} →
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why us */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Warum PhoneMedic?</h2>
            <p className="text-gray-500">Langjährige Erfahrung und modernste Technik</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyUs.map((item, index) => (
              <div key={index} className="text-center p-6 rounded-xl bg-gray-50 border border-gray-100">
                <h3 className="text-base font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-500">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">So funktioniert's</h2>
            <p className="text-gray-500">In 4 einfachen Schritten zur Reparatur</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { step: '1', title: 'Gerät & Reparatur wählen', description: 'Gerät auswählen, Reparatur buchen. Alle Preise sind transparent angegeben.' },
              { step: '2', title: 'Versandlabel erhalten', description: 'Direkt nach der Bestellung erhältst du ein kostenloses DHL-Versandlabel.' },
              { step: '3', title: 'Gerät einsenden', description: 'Sicher verpacken und mit dem kostenlosen Label einsenden.' },
              { step: '4', title: 'Repariert zurückbekommen', description: 'Wir reparieren dein Gerät und schicken es versichert zurück.' },
            ].map((item, index) => (
              <div key={index} className="text-center flex flex-col items-center">
                <div className="w-12 h-12 bg-gray-900 text-white rounded-full flex items-center justify-center text-lg font-bold mb-4">
                  {item.step}
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-500">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <ReviewsSection />

      {/* CTA */}
      <section className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-3">Gerät kaputt? Wir reparieren es.</h2>
          <p className="text-gray-400 mb-8">Kostenloser Kostenvoranschlag für Platinen-Reparaturen</p>
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => go('/platinen-reparatur')}
              className="bg-indigo-600 text-white px-7 py-3.5 rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
            >
              Platinen-Reparatur anfragen
            </button>
            <button
              onClick={() => go('/repair')}
              className="bg-white text-gray-900 px-7 py-3.5 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Alle Reparaturen & Preise
            </button>
          </div>
        </div>
      </section>
    </div>
  )
}

export default HomePage
